import React, { useState, useEffect, useCallback } from 'react';
import { getVideoUploads, retryProcessing, pollUploadStatus, cancelChunkedUpload } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * Recordings the server is still holding for this video.
 *
 * The chunks live on the server, so these can be finished from any device -
 * including one that never made the recording, or after the browser cache was
 * cleared. Covers processing that failed, processing still running, and chunks
 * that were uploaded but never processed (e.g. the tab died mid-recording).
 */
const PendingUploads = ({ videoId, narrator, refreshToken, onRecovered }) => {
  const { isAdmin } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setSessions(await getVideoUploads(videoId, narrator));
    } catch (err) {
      console.error("Failed to load pending uploads:", err);
    } finally {
      setIsLoading(false);
    }
  }, [videoId, narrator]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // Follow anything the server is already working on, so the narrator sees it
  // finish instead of being tempted to start over. A stale session's worker is
  // gone and nothing will ever move it, so polling it would spin forever -
  // leave it for the retry button instead.
  useEffect(() => {
    const processing = sessions.find(session => session.status === 'processing' && !session.is_stale);
    if (!processing || busyId) return;

    let cancelled = false;
    (async () => {
      try {
        const finished = await pollUploadStatus(processing.upload_id, { shouldStop: () => cancelled });
        if (cancelled || !finished) return;
        onRecovered(finished);
      } catch (err) {
        if (!cancelled) setError(`ประมวลผลไม่สำเร็จ: ${err.message}`);
      } finally {
        if (!cancelled) load();
      }
    })();

    return () => { cancelled = true; };
  }, [sessions, busyId, load, onRecovered]);

  const handleProcess = async (session) => {
    if (session.duplicate_risk) {
      const confirmed = window.confirm(
        'เสียงของการอัดครั้งนี้อาจถูกเพิ่มเข้าไฟล์เรียบร้อยแล้ว หากประมวลผลอีกครั้ง เสียงอาจซ้ำสองรอบ\n\nต้องการดำเนินการต่อหรือไม่?'
      );
      if (!confirmed) return;
    }

    setError(null);
    setBusyId(session.upload_id);
    try {
      const finished = await retryProcessing(session.upload_id, { force: session.duplicate_risk });
      onRecovered(finished);
      setSessions(prev => prev.filter(item => item.upload_id !== session.upload_id));
    } catch (err) {
      console.error("Processing failed:", err);
      setError(`ประมวลผลไม่สำเร็จ: ${err.message}`);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const handleDiscard = async (session) => {
    if (!window.confirm('ต้องการลบเสียงที่ค้างอยู่บนเซิร์ฟเวอร์นี้หรือไม่? เสียงนี้จะกู้คืนไม่ได้อีก')) return;

    setBusyId(session.upload_id);
    try {
      await cancelChunkedUpload(session.upload_id);
      setSessions(prev => prev.filter(item => item.upload_id !== session.upload_id));
    } catch (err) {
      setError(`ลบไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading && sessions.length === 0) return null;
  if (sessions.length === 0) return null;

  return (
    <div className="pending-uploads mt-4">
      <h5>เสียงที่ค้างอยู่บนเซิร์ฟเวอร์</h5>
      <p className="text-muted small">
        {isAdmin
          ? 'เสียงเหล่านี้อัพโหลดขึ้นเซิร์ฟเวอร์แล้วแต่ยังประมวลผลไม่สำเร็จ สามารถกดประมวลผลใหม่ได้จากเครื่องใดก็ได้'
          : 'เสียงเหล่านี้อัพโหลดขึ้นเซิร์ฟเวอร์แล้วแต่ยังประมวลผลไม่สำเร็จ กรุณาแจ้งผู้ดูแลให้ประมวลผลใหม่'}
      </p>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="list-group">
        {sessions.map((session) => {
          const isBusy = busyId === session.upload_id;
          // A stale session says "processing" but nothing is working on it, so
          // it must not read or behave as live. is_stale comes from the server,
          // which owns the rule - re-deriving it here is what previously greyed
          // out a retry that /complete would have accepted.
          const isLiveProcessing = session.status === 'processing' && !session.is_stale;
          const isProcessing = isLiveProcessing || isBusy;
          const isLocked = !!busyId || isLiveProcessing;

          return (
            <div key={session.upload_id} className="list-group-item">
              <div className="d-flex w-100 justify-content-between align-items-start">
                <div>
                  <strong>{new Date(session.created_at).toLocaleString('th-TH')}</strong>
                  <div className="small text-muted">
                    {session.usable_chunks} ส่วน
                    {!session.is_finalized && ' (การอัดถูกขัดจังหวะ - กู้คืนเท่าที่อัพโหลดไว้)'}
                  </div>
                  {session.status === 'failed' && session.error_message && (
                    <div className="small text-danger">ผิดพลาด: {session.error_message}</div>
                  )}
                  {session.status === 'processing' && session.is_stale && (
                    <div className="small text-danger">การประมวลผลถูกขัดจังหวะ กรุณาประมวลผลใหม่</div>
                  )}
                  {session.duplicate_risk && (
                    <div className="small text-warning">
                      ⚠️ เสียงนี้อาจถูกเพิ่มเข้าไฟล์แล้ว ประมวลผลซ้ำอาจทำให้เสียงซ้ำ
                    </div>
                  )}
                </div>
                {/* Retry / discard act on a server-side session — admin only.
                    Narrators still see the session and its live processing state. */}
                {isAdmin ? (
                  <div className="d-flex flex-column gap-1">
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleProcess(session)}
                      disabled={isLocked}
                    >
                      {isProcessing
                        ? <><LoadingSpinner small /> กำลังประมวลผล...</>
                        : 'ประมวลผลใหม่'}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDiscard(session)}
                      disabled={isLocked}
                    >
                      ลบ
                    </button>
                  </div>
                ) : (
                  isProcessing && (
                    <div className="small text-muted d-flex align-items-center">
                      <LoadingSpinner small /> กำลังประมวลผล...
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PendingUploads;
